import { useCallback, useEffect, useState } from 'react'

/**
 * User-facing orientation preference, persisted to localStorage.
 *
 * `'auto'` defers to the matchMedia-based detection in
 * `useViewportOrientation`. `'landscape'` / `'portrait'` force the value
 * regardless of the device viewport.
 */
export type OrientationPref = 'auto' | 'landscape' | 'portrait'

export const ORIENTATION_PREF_STORAGE_KEY = 'sagwa.orientationPref'

/** Custom DOM event broadcast when the preference changes, used by the hook. */
const ORIENTATION_PREF_EVENT = 'sagwa:orientation-pref-changed'

const VALID_VALUES: ReadonlyArray<OrientationPref> = ['auto', 'landscape', 'portrait']

function isOrientationPref(v: unknown): v is OrientationPref {
  return typeof v === 'string' && (VALID_VALUES as ReadonlyArray<string>).includes(v)
}

/**
 * Read the persisted orientation preference. Unknown / missing values coerce
 * to `'auto'`. Safe on SSR — returns `'auto'` when `window` is undefined.
 *
 * Note: `localStorage.getItem` can throw on sandboxed Telegram WebView contexts
 * (rare but observed). We swallow the error so the lobby never crashes.
 */
export function getOrientationPref(): OrientationPref {
  if (typeof window === 'undefined') return 'auto'
  try {
    const raw = window.localStorage.getItem(ORIENTATION_PREF_STORAGE_KEY)
    return isOrientationPref(raw) ? raw : 'auto'
  } catch {
    return 'auto'
  }
}

/**
 * Write the orientation preference. Broadcasts a `sagwa:orientation-pref-changed`
 * DOM event so listeners in the same tab can react synchronously (the native
 * `storage` event only fires across tabs).
 *
 * If `localStorage.setItem` throws (quota / sandbox), the event still fires so
 * the in-memory state stays consistent for the current session.
 */
export function setOrientationPref(p: OrientationPref): void {
  if (typeof window === 'undefined') return
  try {
    window.localStorage.setItem(ORIENTATION_PREF_STORAGE_KEY, p)
  } catch {
    // Fall through — broadcast the event anyway so in-tab listeners update.
  }
  try {
    window.dispatchEvent(new CustomEvent<OrientationPref>(ORIENTATION_PREF_EVENT, { detail: p }))
  } catch {
    /* no-op */
  }
}

/**
 * React hook returning `[pref, setPref]`. Subscribes to both the in-tab custom
 * event and the cross-tab `storage` event so the toggle stays consistent across
 * windows.
 */
export function useOrientationPref(): [OrientationPref, (p: OrientationPref) => void] {
  const [pref, setPref] = useState<OrientationPref>(() => getOrientationPref())

  useEffect(() => {
    if (typeof window === 'undefined') return
    const onCustom = (ev: Event): void => {
      const detail = (ev as CustomEvent<OrientationPref>).detail
      if (isOrientationPref(detail)) setPref(detail)
    }
    const onStorage = (ev: StorageEvent): void => {
      if (ev.key !== ORIENTATION_PREF_STORAGE_KEY) return
      setPref(isOrientationPref(ev.newValue) ? ev.newValue : 'auto')
    }
    window.addEventListener(ORIENTATION_PREF_EVENT, onCustom as EventListener)
    window.addEventListener('storage', onStorage)
    return () => {
      window.removeEventListener(ORIENTATION_PREF_EVENT, onCustom as EventListener)
      window.removeEventListener('storage', onStorage)
    }
  }, [])

  const update = useCallback((p: OrientationPref): void => {
    setOrientationPref(p)
    // Update local state immediately — the custom event listener will also
    // fire but updating here keeps the toggle snappy.
    setPref(p)
  }, [])

  return [pref, update]
}
